import React, { useState, useEffect } from 'react';
import { 
  X, Zap, Layout, Image as ImageIcon, Type, 
  Send, Copy, RefreshCw, Globe, 
  Tv, Monitor, MonitorSmartphone, Check, Loader2,
  MousePointerClick, Megaphone, ArrowRight
} from 'lucide-react';

const AD_PLATFORMS = [
  { id: 'meta', name: 'Meta (FB/IG)', icon: Globe, color: 'text-blue-500' },
  { id: 'google_search', name: 'Google Search', icon: Globe, color: 'text-emerald-500' },
  { id: 'youtube', name: 'YouTube', icon: Tv, color: 'text-rose-500' },
  { id: 'native', name: 'Native (Taboola)', icon: Megaphone, color: 'text-orange-500' }
];

const AdStudioModal = ({ isOpen, onClose, campaignData, isEmbedded = false, initialPlatform = null, showCloseButton = false }) => {
  const [activePlatform, setActivePlatform] = useState(initialPlatform || 'meta');

  useEffect(() => {
    if (initialPlatform) {
      setActivePlatform(initialPlatform);
    }
  }, [initialPlatform]);

  const [activeTab, setActiveTab] = useState('edit'); // edit, preview
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('mobile');

  const [customImagePrompt, setCustomImagePrompt] = useState('');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const displayDomain = (campaignData?.customDomain && campaignData.customDomain !== 'xpgo.link') ? campaignData.customDomain : 'xpgo.link';
  const displayPath = `/u/${campaignData?.username || 'user'}/${campaignData?.slug || ''}`;
  
  const [adContent, setAdContent] = useState({
    meta: {
      primaryText: "Struggling to find the right solution? Our AI-powered system helps you scale faster than ever. See why thousands of affiliates are switching to Ninja.",
      headline: "The Future of Affiliate Marketing is Here",
      cta: "Learn More",
      image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1000&auto=format&fit=crop"
    },
    google_search: {
      headlines: ["#1 Affiliate Builder", "Scale Your Offers Fast", "AI Marketing Engine"],
      descriptions: ["Build high-converting funnels in seconds with our agentic AI.", "The most advanced platform for professional affiliate marketers."],
      path1: "start",
      path2: "now"
    },
    native: {
      headline: "The One Mistake Affiliates Make When Scaling (Stop Now)",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1000&auto=format&fit=crop",
      brandName: "Ninja Insights"
    }
  });

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const res = await fetch('/api/creatives/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform: activePlatform, campaignData })
      });
      const data = await res.json();
      if (!data.error) {
        setAdContent(prev => ({
          ...prev,
          [activePlatform]: { ...prev[activePlatform], ...data }
        }));
      }
    } catch (err) {
      console.error('Failed to generate ad content:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!customImagePrompt.trim()) return alert('Please enter an image concept or description first!');
    setIsGeneratingImage(true);
    try {
      const res = await fetch('/api/creatives/generate-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: customImagePrompt, platform: activePlatform })
      });
      const data = await res.json();
      if (data.image) {
        setAdContent(prev => ({
          ...prev,
          [activePlatform]: { ...prev[activePlatform], image: data.image }
        }));
        setCustomImagePrompt('');
      } else {
        alert('Failed to generate image: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Failed to generate custom ad image:', err);
      alert('Error generating ad image.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  if (!isOpen && !isEmbedded) return null;

  const currentPlatform = AD_PLATFORMS.find(p => p.id === activePlatform);

  const StudioContent = (
    <div className={`${isEmbedded ? 'w-full h-full' : 'bg-slate-900 border border-slate-700/50 rounded-[2.5rem] w-full max-w-6xl h-[85vh] shadow-[0_0_100px_rgba(59,130,246,0.1)] transform animate-in zoom-in-95 duration-300'} overflow-hidden flex flex-col`}>
      
      {/* Header */}
      <div className={`px-8 py-6 border-b border-slate-800 flex items-center justify-between shrink-0 ${isEmbedded ? 'bg-slate-900' : 'bg-slate-900/50'}`}>
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-blue-600/20 text-blue-400 rounded-2xl border border-blue-500/20 shadow-inner">
            <Zap size={24} />
          </div>
          <div>
            <h3 className="text-xl font-black text-white tracking-tight uppercase">Creative Studio</h3>
            <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] mt-0.5">AI Ad Builder • {campaignData?.name || 'New Campaign'}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-6">
          <div className="flex bg-slate-950/50 p-1 rounded-xl border border-slate-800">
            <button 
              onClick={() => setPreviewDevice('mobile')}
              className={`p-2 rounded-lg transition-all ${previewDevice === 'mobile' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <MonitorSmartphone size={18} />
            </button>
            <button 
              onClick={() => setPreviewDevice('desktop')}
              className={`p-2 rounded-lg transition-all ${previewDevice === 'desktop' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              <Monitor size={18} />
            </button>
          </div>
          {(showCloseButton || !isEmbedded) && onClose && (
            <button onClick={onClose} className="p-3 text-slate-500 hover:text-white hover:bg-slate-800 rounded-2xl transition-all">
              <X size={24} />
            </button>
          )}
        </div>
      </div>

      {/* Platform Selection Bar */}
      <div className="px-8 py-3 border-b border-slate-800 flex items-center space-x-2 bg-slate-900/50 shrink-0 overflow-x-auto custom-scrollbar">
        <div className="flex items-center space-x-1">
          {AD_PLATFORMS.map(platform => (
            <button
              key={platform.id}
              onClick={() => setActivePlatform(platform.id)}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all border shrink-0 group ${
                activePlatform === platform.id 
                ? 'bg-blue-600/10 border-blue-500/50 text-white shadow-inner' 
                : 'border-transparent text-slate-400 hover:bg-slate-800 hover:text-slate-200'
              }`}
            >
              <platform.icon size={16} className={activePlatform === platform.id ? platform.color : 'text-slate-500 group-hover:text-slate-300'} />
              <span className="text-[11px] font-black uppercase tracking-wider">{platform.name}</span>
            </button>
          ))}
        </div>
        <div className="h-6 w-px bg-slate-800 mx-4 hidden sm:block"></div>
        <div className="hidden lg:flex items-center space-x-3 text-[10px] text-slate-500 font-black uppercase tracking-widest">
          <Zap size={12} className="text-blue-400" />
          <span>Active Strategy: {campaignData?.niche || 'Standard'} AI Optimization</span>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Editor Side */}
          <div className="w-1/2 p-8 overflow-y-auto custom-scrollbar border-r border-slate-800">
            <div className="flex items-center justify-between mb-8">
              <h4 className="text-sm font-black text-white uppercase tracking-widest flex items-center">
                <Type size={16} className="mr-2 text-blue-400" /> Ad Content
              </h4>
              <button 
                onClick={handleGenerate}
                disabled={isGenerating}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-[10px] font-black rounded-xl uppercase tracking-widest transition-all shadow-lg shadow-blue-500/20"
              >
                {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                <span>{isGenerating ? 'Generating...' : 'Regenerate'}</span>
              </button>
            </div>

            {activePlatform === 'meta' && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Primary Text</label>
                  <textarea 
                    value={adContent.meta.primaryText}
                    onChange={e => setAdContent({...adContent, meta: {...adContent.meta, primaryText: e.target.value}})}
                    className="w-full bg-slate-950 border border-slate-800 text-white p-4 rounded-2xl text-sm leading-relaxed focus:border-blue-500 outline-none transition-all min-h-[120px] resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Headline</label>
                  <input 
                    type="text"
                    value={adContent.meta.headline}
                    onChange={e => setAdContent({...adContent, meta: {...adContent.meta, headline: e.target.value}})}
                    className="w-full bg-slate-950 border border-slate-800 text-white px-4 py-3 rounded-2xl text-sm focus:border-blue-500 outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Call to Action</label>
                  <select 
                    value={adContent.meta.cta}
                    onChange={e => setAdContent({...adContent, meta: {...adContent.meta, cta: e.target.value}})}
                    className="w-full bg-slate-950 border border-slate-800 text-white px-4 py-3 rounded-2xl text-sm focus:border-blue-500 outline-none transition-all appearance-none"
                  >
                    <option>Learn More</option>
                    <option>Shop Now</option>
                    <option>Sign Up</option>
                    <option>Download</option>
                  </select>
                </div>
              </div>
            )}

            {activePlatform === 'google_search' && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Headlines (RSAs)</label>
                  {adContent.google_search.headlines.map((h, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <span className="text-[10px] font-mono text-slate-600 w-4">{i+1}</span>
                      <input 
                        type="text"
                        value={h}
                        onChange={e => {
                          const newHeadlines = [...adContent.google_search.headlines];
                          newHeadlines[i] = e.target.value;
                          setAdContent({...adContent, google_search: {...adContent.google_search, headlines: newHeadlines}});
                        }}
                        className="flex-1 bg-slate-950 border border-slate-800 text-white px-4 py-2.5 rounded-xl text-sm focus:border-blue-500 outline-none transition-all"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {(activePlatform === 'meta' || activePlatform === 'native') && (
              <div className="mt-12 p-6 bg-slate-950/50 border border-dashed border-slate-800 rounded-[2rem] flex flex-col items-center justify-center text-center space-y-4">
                <div className="p-4 bg-slate-900 rounded-2xl text-slate-500">
                  <ImageIcon size={32} />
                </div>
                <div className="w-full">
                  <h5 className="text-xs font-bold text-white mb-1">AI Graphic Builder (Nano Banana Pro)</h5>
                  <p className="text-[10px] text-slate-500 mb-4">Enter an image concept (e.g. "isometric affiliate marketing nodes with neon glowing lines") to generate a premium ad graphic instantly.</p>
                  
                  <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
                    <input 
                      type="text"
                      placeholder="Describe your custom ad graphic concept..."
                      value={customImagePrompt}
                      onChange={e => setCustomImagePrompt(e.target.value)}
                      className="flex-1 bg-slate-950 border border-slate-850 text-white px-4 py-2.5 rounded-xl text-xs focus:border-blue-500 outline-none transition-all placeholder-slate-650"
                    />
                    <button 
                      onClick={handleGenerateImage}
                      disabled={isGeneratingImage}
                      className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-[10px] font-black rounded-xl uppercase tracking-widest transition-all shadow-md shrink-0 flex items-center justify-center gap-1.5"
                    >
                      {isGeneratingImage ? <Loader2 size={12} className="animate-spin" /> : <span>🎨</span>}
                      <span>{isGeneratingImage ? 'Building...' : 'Build AI Image'}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Preview Side */}
          <div className="flex-1 p-8 bg-slate-950/50 flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <h4 className="text-sm font-black text-white uppercase tracking-widest flex items-center">
                <Layout size={16} className="mr-2 text-indigo-400" /> Real-time Preview
              </h4>
              <div className="flex space-x-2">
                <button className="p-2 text-slate-500 hover:text-white transition-colors">
                  <Copy size={16} />
                </button>
                <button className="p-2 text-slate-500 hover:text-white transition-colors">
                  <Send size={16} />
                </button>
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center">
              {/* Meta Preview */}
              {activePlatform === 'meta' && (
                <div className={`bg-slate-900 w-full max-w-[340px] rounded-xl overflow-hidden border border-slate-800 shadow-2xl transition-all duration-500 ${previewDevice === 'mobile' ? 'scale-100' : 'scale-110'}`}>
                  {/* Header */}
                  <div className="p-3 flex items-center space-x-3">
                    <div className="size-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xs">N</div>
                    <div>
                      <div className="text-[11px] font-bold text-white">Ninja Brand</div>
                      <div className="text-[9px] text-slate-500 flex items-center">Sponsored • <Globe size={8} className="ml-1" /></div>
                    </div>
                  </div>
                  {/* Body */}
                  <div className="px-3 pb-3 text-xs text-slate-200 line-clamp-3">
                    {adContent.meta.primaryText}
                  </div>
                  {/* Media */}
                  <div className="aspect-square bg-slate-800 relative">
                    <img src={adContent.meta.image} alt="Ad Content" className="w-full h-full object-cover" />
                  </div>
                  {/* Footer */}
                  <div className="p-3 bg-slate-800/50 flex items-center justify-between">
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="text-[10px] text-slate-400 uppercase font-bold tracking-tight truncate">{displayDomain}</div>
                      <div className="text-[13px] font-bold text-white truncate">{adContent.meta.headline}</div>
                    </div>
                    <button className="px-4 py-2 bg-slate-700 text-white text-[11px] font-bold rounded-md whitespace-nowrap">
                      {adContent.meta.cta}
                    </button>
                  </div>
                </div>
              )}

              {/* Google Search Preview */}
              {activePlatform === 'google_search' && (
                 <div className="w-full max-w-[400px] bg-slate-900 rounded-3xl p-6 border border-slate-800 shadow-2xl">
                   <div className="flex items-center space-x-2 mb-3">
                     <div className="p-2 bg-white rounded-lg"><Globe size={14} className="text-slate-900" /></div>
                     <div className="text-[11px] text-slate-400 font-medium">Sponsored • https://{displayDomain}{displayPath}</div>
                   </div>
                   <div className="text-blue-400 text-lg font-medium mb-2 hover:underline cursor-pointer">
                     {adContent.google_search.headlines[0]} | {adContent.google_search.headlines[1]}
                   </div>
                   <div className="text-slate-400 text-xs leading-relaxed">
                     {adContent.google_search.descriptions[0]}
                   </div>
                 </div>
              )}

              {/* Native Preview */}
              {activePlatform === 'native' && (
                <div className="w-full max-w-[300px] bg-slate-900 rounded-xl overflow-hidden border border-slate-800 group cursor-pointer shadow-2xl">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={adContent.native.image} alt="Native Ad" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-4 space-y-2">
                    <h5 className="text-sm font-bold text-white leading-tight">{adContent.native.headline}</h5>
                    <div className="flex items-center justify-between text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                      <span>{adContent.native.brandName}</span>
                      <span className="px-1.5 py-0.5 border border-slate-700 rounded">Sponsored</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Actions */}
            <div className="mt-8 pt-8 border-t border-slate-800 flex items-center justify-end space-x-4">
               <button className="px-6 py-3 text-slate-400 hover:text-white font-bold text-sm transition-colors">
                 Save Draft
               </button>
               <button className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl shadow-lg shadow-blue-500/20 flex items-center space-x-2 transition-all active:scale-95">
                 <Check size={18} />
                 <span className="uppercase tracking-widest text-xs">Deploy Creative</span>
               </button>
            </div>
        </div>
      </div>
    </div>
  );

  if (isEmbedded) return StudioContent;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/95 backdrop-blur-xl animate-in fade-in duration-300">
      {StudioContent}
    </div>
  );
};

export default AdStudioModal;
